// src/components/Finanzas/utils/descargarPdfHelper.js
// Centraliza la descarga del PDF de una RelacionGastos en cliente. Se invoca
// desde RevisionCierre.jsx, MiHistorialBolsillo.jsx y DetalleRelacion.jsx
// para evitar duplicar la logica de hidratacion + manejo de errores.
//
// Estrategia:
//   1. Si la pantalla ya tiene `relacion` (objeto completo) lo reutiliza.
//   2. Si solo tiene `relacionId`, llama obtenerRelacion(id) primero.
//   3. Hidrata en paralelo gastos, entregas, traslados y comprobantes por
//      `_id.$in` (los endpoints existen).
//   4. Invoca PdfRelacionCerrada(infoRelacion) que genera y descarga el PDF.
//   5. En caso de error, alerta con Alertassw + extractError.

import {
  obtenerRelacion,
  listarGastos,
  listarEntregas,
  listarTraslados,
  listarComprobantes
} from '../../../api/finanzas';
import { getDataObtenerUno } from '../../../api/common';
import { Alertassw } from '../../Alertas/Alertassw';
import { extractError } from './extractError';
import { PdfRelacionCerrada } from '../../Pdfs/PdfRelacionCerrada';

const resolveEmptyList = () => Promise.resolve({ data: { data: [] } });

// Trae la firma persistida del usuario desde la coleccion Signs (formato
// { user, sign }). Mismo wrapper que usa PdfActa.js. Si el usuario no tiene
// firma, devuelve null (el PDF se imprime sin firma sin romper).
const obtenerFirmaUsuario = async (usuarioId) => {
  if (!usuarioId) return null;
  try {
    const resp = await getDataObtenerUno('sign', { user: usuarioId });
    return resp?.data?.data?.sign || null;
  } catch (err) {
    console.warn('[descargarPdfHelper] No se pudo obtener firma de', usuarioId, err?.message);
    return null;
  }
};

/**
 * Descarga el PDF de una relacion en cliente.
 *
 * @param {object} params
 * @param {string} [params.relacionId]  Id de la relacion. Requerido si no se
 *                                      pasa `relacion`.
 * @param {object} [params.relacion]    Relacion ya hidratada (opcional, ahorra
 *                                      una peticion).
 * @returns {Promise<void>}
 */
export async function descargarPdfRelacionLocal({ relacionId, relacion: relacionInicial } = {}) {
  try {
    let relacion = relacionInicial;
    if (!relacion) {
      if (!relacionId) {
        throw new Error('Falta el id de la relación');
      }
      const respRel = await obtenerRelacion(relacionId);
      relacion = respRel?.data?.data;
    }
    if (!relacion || !relacion._id) {
      throw new Error('Relación no encontrada');
    }

    const id = relacion._id;
    const gastosIds    = relacion.gastosIncluidos    || [];
    const entregasIds  = relacion.entregasIncluidas  || [];
    const trasladosIds = relacion.trasladosIncluidos || [];

    // Ids para hidratar firmas: SIEMPRE la del responsable (Elabora). La del
    // aprobador solo si la relacion ya fue aprobada (campo aprobadoPor del
    // schema). En BORRADOR el aprobador queda en blanco.
    const responsableId = relacion.responsable?._id || relacion.responsable;
    const aprobadoPorId = relacion.aprobadoPor?._id || relacion.aprobadoPor;

    const [
      gastosResp, entregasResp, trasladosResp, comprobantesResp,
      firmaResponsable, firmaAprobador
    ] = await Promise.all([
      gastosIds.length
        ? listarGastos({ conditions: { _id: { $in: gastosIds } }, sort: { fecha: -1 } })
        : resolveEmptyList(),
      entregasIds.length
        ? listarEntregas({ conditions: { _id: { $in: entregasIds } }, sort: { fecha: -1 } })
        : resolveEmptyList(),
      trasladosIds.length
        ? listarTraslados({ conditions: { _id: { $in: trasladosIds } }, sort: { fecha: -1 } })
        : resolveEmptyList(),
      listarComprobantes({ relacionGastos: id }).catch(() => ({ data: { data: [] } })),
      obtenerFirmaUsuario(responsableId),
      aprobadoPorId ? obtenerFirmaUsuario(aprobadoPorId) : Promise.resolve(null)
    ]);

    PdfRelacionCerrada({
      relacion,
      gastos:       gastosResp?.data?.data    || [],
      entregas:     entregasResp?.data?.data  || [],
      traslados:    trasladosResp?.data?.data || [],
      comprobantes: comprobantesResp?.data?.data || [],
      firmaResponsable,
      firmaAprobador
    });
  } catch (err) {
    console.error('[descargarPdfHelper] Error generando PDF:', err);
    Alertassw('error', 'Error generando PDF', extractError(err, 'No se pudo generar el PDF de la relación'));
  }
}
